<script lang="ts">
  // 5 · Rounding: off, or round to the nearest / up / down per increment,
  // with a live example.
  import { fly } from 'svelte/transition';
  import { ArrowRight } from '@lucide/svelte';
  import { t } from '../../i18n.svelte';
  import { formatDuration } from '../../format';
  import { ChipGroup, Field, Notice, Segmented, Toggle } from '../../ui';
  import StepIntro from './StepIntro.svelte';
  import { flow, ROUND_INCREMENTS } from '../state.svelte';

  const EXAMPLE_MINUTES = 68;
  const increments = $derived(ROUND_INCREMENTS.map((inc) => ({ value: inc, label: t('onb.round.minutes', { count: inc }) })));
  const rounded = $derived.by(() => {
    const q = EXAMPLE_MINUTES / flow.roundInc;
    const n = flow.roundMode === 'up' ? Math.ceil(q) : flow.roundMode === 'down' ? Math.floor(q) : Math.round(q);
    return n * flow.roundInc;
  });
  const slide = $derived({ y: flow.still ? 0 : -6, duration: flow.still ? 0 : 180 });
</script>

<StepIntro kicker={t('onb.round.kicker')} title={t('onb.round.title')} lead={t('onb.round.text')} />
<div class="stack">
  <div class="switch">
    <Toggle bind:checked={flow.roundOn} label={t('onb.round.enable')} description={t('onb.round.enable_text')} />
  </div>

  {#if flow.roundOn}
    <div class="stack" transition:fly={slide}>
      <Field label={t('onb.round.mode')}>
        <Segmented
          value={flow.roundMode}
          options={[
            { value: 'nearest', label: t('onb.round.nearest') },
            { value: 'up', label: t('onb.round.up') },
            { value: 'down', label: t('onb.round.down') }
          ]}
          full
          ariaLabel={t('onb.round.mode')}
          onchange={(v) => (flow.roundMode = v)}
        />
      </Field>
      <Field label={t('onb.round.increment')}>
        <ChipGroup options={increments} selected={[flow.roundInc]} ariaLabel={t('onb.round.increment')} onchange={(v) => (flow.roundInc = v[0] ?? flow.roundInc)} />
      </Field>
      <div class="example" aria-live="polite">
        <span class="eyebrow label">{t('onb.round.example')}</span>
        <span class="from tabular">{formatDuration(EXAMPLE_MINUTES, 'hm')}</span>
        <span class="arrow"><ArrowRight size={16} /><span class="sr-only">{t('onb.round.becomes')}</span></span>
        {#key rounded}
          <span class="to tabular" in:fly={{ y: flow.still ? 0 : 6, duration: flow.still ? 0 : 200 }}>{formatDuration(rounded, 'hm')}</span>
        {/key}
      </div>
    </div>
  {:else}
    <Notice variant="dashed" tone="neutral" icon={false}>{t('onb.round.off')}</Notice>
  {/if}
</div>

<style>
  .switch { padding: var(--space-3) var(--space-4); border: 1px solid var(--border); border-radius: var(--radius); background: var(--surface-2); }

  .example {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    border: 1px solid var(--border-brand);
    border-radius: var(--radius);
    background: var(--brand-softer);
  }
  .label { margin-right: auto; }
  .from { font-size: var(--text-xl); font-weight: var(--weight-semibold); color: var(--text-3); text-decoration: line-through; text-decoration-thickness: 2px; }
  .arrow { display: inline-flex; color: var(--brand); }
  .to { display: inline-block; font-size: var(--text-2xl); font-weight: var(--weight-bold); letter-spacing: var(--tracking-tight); color: var(--brand); }

  @media (max-width: 400px) {
    .label { display: none; }
  }
</style>
